import React, { useState, useEffect, useContext } from 'react';
import '../../App.css';
import './data-management.css';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';

// icons
import downArrowDarkIcon from '../../icons/down-arrow-dark.256.png';
import upArrowDarkIcon from '../../icons/up-arrow-dark.256.png';
import downArrowLightIcon from '../../icons/down-arrow-light.256.png';
import upArrowLightIcon from '../../icons/up-arrow-light.256.png';
import {
  CircleAlert,
  CloudDownload,
  Copy,
  File,
  Files,
  Flag,
  FolderTree,
  HardDrive,
  Info,
  LockKeyhole,
  LockKeyholeOpen,
  PackageOpen,
  RefreshCw,
  ScrollText,
  ShelvingUnit,
  Trash2,
} from 'lucide-react';

// types
import {
  ComputeServiceType,
  FileBrowserPage,
  GatewayLogType,
  JobItemType,
  JobType,
  Site,
  SiteStorageArea,
  ToolType,
  toolTypeToPath,
  UserType,
} from '../../utils/types';

// functions
import { apiPrefix, getStorageAreaFromID, siteName } from '../../utils/functions';
import { addLineBreaks } from '../../utils/functions';

// classes
import { displayFileSize } from '../../utils/functions';
import Scrollbox from '../../tools/scrollbox';
import ComputeSession from '../compute-tiles/compute-session';
import MiniButton from '../../tools/mini-button';
import StatusIndicator from '../../tools/status-indicator';
import PleaseWait from '../../tools/please-wait';
import { ToolButtonType } from '../../tools/tool-button';
import ToolButton from '../../tools/tool-button';
import SiteIcon from '../../tools/site-icon';
import {
  Event,
  EventType,
  EventTarget,
  InitiateComputeSearchParams,
  InitiateDMSearchParams,
} from '../../utils/events';
import { useNavigate } from 'react-router-dom';
import { handleEvent } from '../../app-actions';

//  --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

// import state definition and functions.
import { setDisplayedStagingJob } from './file-browser-slice';

// app state definitions.
import { setSelectedTool } from '../../app-slice';

//  --------------------------------------------------------------------------
//
//  T Y P E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  P R O P E R T I E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  C L A S S   D E F I N I T I O N
//
//  --------------------------------------------------------------------------

type AccessState = 'accessible' | 'inaccessible' | 'checking';

function AccessLockIcon(props: { accessState: AccessState }) {
  //
  const { t } = useTranslation();

  if (props.accessState === 'accessible') {
    return (
      <LockKeyholeOpen
        className="text-colour"
        data-text-green="T"
        width={30}
        height={30}
        strokeWidth={1.5}
      />
    );
  }
  if (props.accessState === 'inaccessible') {
    return (
      <LockKeyhole
        className="text-colour"
        data-text-red="T"
        width={30}
        height={30}
        strokeWidth={1.5}
      />
    );
  }
  return (
    <i
      className="pi pi-spin pi-spinner srctx-info"
      style={{
        fontSize: '1.8rem',
      }}
      title={t('Checking access to namespace')}
    />
  );
  //
} // AccessLockIcon

//  -------------------------------------------------
//
//  Display a single file item.
//
//  -------------------------------------------------

function Item(props: { item: JobItemType; type: string; expanded: boolean }) {
  //
  return (
    <div
      className="fr"
      style={{
        marginBottom: '15px',
        flex: '0 0 auto',
        width: '100%',
      }}
    >
      <div
        className="fr"
        style={{
          flex: '0 0 40px',
          marginLeft: '70px',
          alignItems: 'center',
        }}
      >
        {(() => {
          switch (props.type.toLowerCase()) {
            case 'dataset':
              return <PackageOpen width={25} height={25} strokeWidth={1.5} />;
            case 'container':
              return <ShelvingUnit width={25} height={25} strokeWidth={1.5} />;
            default:
              return <File width={25} height={25} strokeWidth={1.5} />;
          }
        })()}
      </div>
      <div
        style={{
          flex: '1 1',
        }}
      >
        {props.item.name}
      </div>
      <div
        style={{
          flex: '0 0 100px',
        }}
      >
        {props.type}
      </div>
      <div
        style={{
          flex: '0 0 150px',
        }}
      >
        {props.item.bytes === 0
          ? 'size unavailable'
          : displayFileSize({ bytes: props.item.bytes })}
      </div>
      <div
        style={{
          flex: '0 0 80px',
        }}
      >
        <AccessLockIcon
          accessState={
            props.item.unauthorised === false
              ? 'accessible'
              : props.item.unauthorised === true
                ? 'inaccessible'
                : 'checking'
          }
        />
      </div>
    </div>
  );
  //
} // Item

//  -------------------------------------------------
//
//  Display a single job item.
//
//  -------------------------------------------------

function JobItem(props: {
  logsFocusRow: React.MutableRefObject<string | undefined>;
  standaloneComponent?: boolean; // default = true
  item: JobType;
  index: number;
}) {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // global state variables.
  const sG_userType: UserType = useAppSelector(
    (state) => state.global.userType ?? UserType.STANDARD,
  );

  // data state variables
  const sD_storageAreas: SiteStorageArea[] = useAppSelector(
    (state) => state.data.storageAreas,
  );

  // file-browser state variables.
  const sDisplayedStagingJob: JobType | undefined = useAppSelector(
    (state) => state.fileBrowser.displayedStagingJob,
  );

  // app state variables.
  const sGatewayLogs: GatewayLogType[] = useAppSelector((state) => state.app.gatewayLogs);

  //  ------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  ------------------------------------------------------------

  const navigate = useNavigate();

  const [sToStorageArea, setToStorageArea] = useState<{
    storageArea: string;
    site: Site | undefined;
  }>({
    storageArea: '',
    site: undefined,
  });

  //  ------------------------------------------------------------
  //
  //  search the data-management API for the files being
  //  transferred by this job ID.
  //
  //  ------------------------------------------------------------

  const initiateDMSearchHandler = (
    event: React.MouseEvent<HTMLButtonElement>,
    pJobID: string,
  ) => {
    //
    // run the data-management search.
    const params: InitiateDMSearchParams = {
      filename: '',
      jobID: pJobID,
      fileType: 'all',
      pageDisplayed: FileBrowserPage.JobDetails,
      showPage: true,
    };
    const newEvent: Event = {
      eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
      eventType: EventType.INITIATE_DM_SEARCH,
      parameters: params,
    };
    dispatch(
      handleEvent({
        event: newEvent,
        ctx: { navigate },
      }),
    );
    //
  }; // initiateDMSearchHandler

  //  ------------------------------------------------------------
  //
  //  H O O K S
  //
  //  ------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  refresh the storage area name and site name when the
  //  storage-area ID changes.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    setToStorageArea(
      getStorageAreaFromID({ storageAreaUUID: props.item.to_storage_area_uuid ?? '' }),
    );
    //
  }, [props.item.to_storage_area_uuid, sD_storageAreas]);

  //  ------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  ------------------------------------------------------------

  return (
    <>
      <div
        key={props.index}
        style={{
          flex: '0 0 auto',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* the job header row */}
        <div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            border: 'none',
            backgroundColor: 'transparent',
            marginBottom: '10px',
          }}
        >
          <div style={{ flex: '0 0 5px' }} />

          <MiniButton
            className="text-colour"
            data-mini-button="T"
            data-selected={
              props.item.job_id === sDisplayedStagingJob?.job_id &&
              props.standaloneComponent !== false
                ? 'T'
                : 'F'
            }
            title="View information"
            onClick={() => dispatch(setDisplayedStagingJob(props.item))}
          >
            <Info width={30} height={30} strokeWidth={1.5} />
          </MiniButton>
          <div style={{ flex: '0 0 5px' }} />

          {[UserType.OPERATOR, UserType.DEVELOPER].includes(sG_userType) && (
            <MiniButton
              className="text-colour"
              data-mini-button="T"
              onClick={(event) =>
                initiateDMSearchHandler(
                  /* event = */ event,
                  /* pJobID = */ props.item.job_id,
                )
              }
              style={{ marginRight: '5px' }}
              title="View data files"
            >
              <FolderTree width={30} height={30} strokeWidth={1.5} />
            </MiniButton>
          )}

          <div
            className="job-row"
            style={{
              flex: '1 1 auto',
              display: 'flex',
              flexDirection: 'row',
              borderRadius: '10px',
              alignItems: 'center',
              height: 'auto',
              marginRight: '10px',
            }}
          >
            <div style={{ flex: '0 0 10px' }} />
            <div
              className="text-colour"
              style={{
                flex: '1 1',
                margin: '0px 0px 0px 0px',
                justifyContent: 'left',
                display: 'flex',
              }}
            >
              {props.item.num_items} item(s) to {siteName({ site: sToStorageArea.site })}
            </div>
            <div style={{ flex: '0 0 5px' }} />

            {/* the indicator of the job status, i.e. MOVING, READY, etc */}
            <StatusIndicator status={props.item.status} />

            <div style={{ flex: '0 0 5px' }} />
          </div>
        </div>
        <div style={{ flex: '0 0 5px' }} />
      </div>
    </>
  );
  //
} // JobItem

//  --------------------------------------------------------------------------
//
//  J O B L I S T   C O M P O N E N T
//
//  Displays the list of staging jobs. When canfarSessionId is provided,
//  only jobs linked to that session are shown.
//
//  --------------------------------------------------------------------------

export function JobList(props: {
  logsFocusRow: React.MutableRefObject<string | undefined>;
  standaloneComponent?: boolean; // default = true
  canfarSessionId?: string;
}) {
  //
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );
  const sAllJobs: JobType[] = useAppSelector(
    (state) => state.fileBrowser.dataManagementJobs,
  );
  const sJobsLoading: boolean = useAppSelector((state) => state.fileBrowser.jobsLoading);

  // when a canfarSessionId is provided, only show jobs linked to that session.
  const sDataManagementJobs: JobType[] =
    props.canfarSessionId !== undefined
      ? sAllJobs.filter((job) => job.skaha_session_id === props.canfarSessionId)
      : sAllJobs;

  async function clearFailedJobs() {
    var urlCommand: string = apiPrefix() + '/v1/data_management/clear_failed_jobs';
    try {
      await fetch(urlCommand, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const newEvent: Event = {
        eventTarget: EventTarget.APP,
        eventType: EventType.GET_DATA_MANAGEMENT_JOBS,
      };
      dispatch(handleEvent({ event: newEvent }));
    } catch (e) {}
  }

  const onClickHandler = (event: React.MouseEvent<HTMLElement>) => {
    const inputBox = event.currentTarget;
    if (inputBox.id === 'refresh-page') {
      const newEvent: Event = {
        eventTarget: EventTarget.APP,
        eventType: EventType.GET_DATA_MANAGEMENT_JOBS,
      };
      dispatch(handleEvent({ event: newEvent }));
    }
    if (inputBox.id === 'clear-failed-jobs') {
      clearFailedJobs();
    }
  };

  const isEmbedded = props.standaloneComponent === false;

  const jobRows = sDataManagementJobs.map((job, index) => (
    <JobItem
      key={index}
      logsFocusRow={props.logsFocusRow}
      item={job}
      index={index}
      standaloneComponent={props.standaloneComponent}
    />
  ));

  if (isEmbedded) {
    return (
      <div className="fc" style={{ width: '100%' }}>
        {props.canfarSessionId === undefined && (
          <div className="panel-header" style={{ flex: '0 0 auto', marginBottom: '8px' }}>
            <div
              id="lbl-data-staging-requests"
              role="heading"
              aria-level={2}
              className="header-text"
              style={{ flex: '1 1 0px' }}
            >
              {t('Status of data-staging requests')}
            </div>
          </div>
        )}
        {sDataManagementJobs.length === 0 && sG_loggedIn === true && (
          <div className="no-data-overlay">No data-staging requests</div>
        )}
        {sG_loggedIn !== true && <div className="no-data-overlay">Login required</div>}
        <div className="fc" style={{ width: '100%' }}>
          {jobRows}
        </div>
      </div>
    );
  }

  return (
    <div
      className={'panel-area fc' + ' br-d-r'}
      style={{
        flex: '1 1',
        maxHeight: '100%',
        height: '100%',
      }}
    >
      <div className="panel-header" style={{ flex: '0 0 auto', marginBottom: '8px' }}>
        <div
          id="lbl-data-staging-requests"
          role="heading"
          aria-level={2}
          className="header-text"
          style={{ flex: '1 1 0px' }}
        >
          {t('Status of data-staging requests')}
        </div>
      </div>

      {sDataManagementJobs.length === 0 && sG_loggedIn === true && (
        <div className="no-data-overlay">No data-staging requests</div>
      )}
      {sG_loggedIn !== true && <div className="no-data-overlay">Login required</div>}
      <Scrollbox
        direction="vertical"
        flex="1 1"
        width="100%"
        flexDirection="column"
        margin="5px"
      >
        {jobRows}
      </Scrollbox>
      <div className="form-button-container">
        <PleaseWait text="Loading jobs....." visible={sJobsLoading} />
        <div style={{ flex: '1 1' }} />
        <MiniButton
          id="refresh-page"
          className="text-colour"
          style={{ flex: '0 0 auto', height: '34px' }}
          onClick={onClickHandler}
          title={t('Refresh')}
          aria-label={t('Refresh')}
          data-mini-button="T"
        >
          <RefreshCw width={30} height={30} strokeWidth={1.5} />
        </MiniButton>
        <div style={{ flex: '0 0 15px' }} />
        <MiniButton
          id="clear-failed-jobs"
          className="text-colour"
          data-selected="F"
          onClick={onClickHandler}
          title={t('Clear failed jobs')}
          aria-label={t('Clear failed jobs')}
          data-mini-button="T"
        >
          <Trash2 width={30} height={30} strokeWidth={1.5} />
        </MiniButton>
      </div>
    </div>
  );
  //
} // JobList

//  --------------------------------------------------------------------------
//
//  J O B D E T A I L S   C O M P O N E N T
//
//  Displays the detail panel for a selected staging job.
//  mode='close' shows a Close button (Data Transfer Jobs page).
//  mode='back'  shows a Back button (Interactive Sessions panel).
//
//  --------------------------------------------------------------------------

export function JobDetails(props: {
  logsFocusRow?: React.MutableRefObject<string | undefined>;
  mode: 'close' | 'back';
}) {
  //
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const sDisplayedStagingJob: JobType | undefined = useAppSelector(
    (state) => state.fileBrowser.displayedStagingJob,
  );
  const sGatewayLogs: GatewayLogType[] = useAppSelector((state) => state.app.gatewayLogs);

  const [sToStorageArea, setToStorageArea] = useState<{
    storageArea: string;
    site: Site | undefined;
  }>({ storageArea: '', site: undefined });

  useEffect(() => {
    setToStorageArea(
      getStorageAreaFromID({
        storageAreaUUID: sDisplayedStagingJob?.to_storage_area_uuid ?? '',
      }),
    );
  }, [sDisplayedStagingJob]);

  const getLogIndexByJobID = (JobID: string) => {
    if (sGatewayLogs.length < 1) return undefined;
    for (var i: number = sGatewayLogs.length - 1; i >= 0; i--) {
      if (sGatewayLogs[i].metadata.job_id === undefined) continue;
      if (sGatewayLogs[i].metadata.job_id === JobID) return sGatewayLogs.length - 1 - i;
    }
    return undefined;
  };

  if (sDisplayedStagingJob === undefined) return null;

  return (
    <div
      className="panel-area fc text-colour"
      style={{
        flex: '1 1',
        maxHeight: '100%',
        height: '100%',
        marginLeft: '10px',
        padding: '10px',
      }}
    >
      <div
        role="heading"
        aria-level={2}
        className="header-text"
        style={{ paddingBottom: '35px' }}
      >
        Details on staging job {sDisplayedStagingJob.job_id}
      </div>
      <div
        className="fr"
        style={{ alignItems: 'center', marginBottom: '20px', width: '100%' }}
      >
        <div style={{ flex: '0 0 80px' }}>
          <SiteIcon site={sToStorageArea.site} size={50} />
        </div>
        <div style={{ flex: '1 1' }}>
          {sDisplayedStagingJob.num_items} item(s) to{' '}
          {siteName({ site: sToStorageArea.site })}
        </div>
      </div>
      <Scrollbox
        direction="vertical"
        flex="1 1"
        width="100%"
        flexDirection="column"
        margin="5px 5px 15px 10px"
      >
        <div
          className="fr"
          style={{ alignItems: 'center', marginBottom: '10px', width: '100%' }}
        >
          <div style={{ flex: '0 0 70px' }}>
            <HardDrive width={30} height={30} strokeWidth={1.5} />
          </div>
          <div style={{ flex: '0 0 180px' }}>Storage area:</div>
          <div style={{ flex: '1 1' }}>{sToStorageArea.storageArea}</div>
        </div>
        <div
          className="fr"
          style={{
            alignItems: 'center',
            marginTop: '15px',
            marginBottom: '10px',
            width: '100%',
          }}
        >
          <div style={{ flex: '0 0 70px' }}>
            <Flag width={30} height={30} strokeWidth={1.5} />
          </div>
          <div style={{ flex: '0 0 180px' }}>Status:</div>
          <div style={{ flex: '1 1' }}>
            <StatusIndicator status={sDisplayedStagingJob.status} />
          </div>
        </div>
        {(sDisplayedStagingJob.error_message?.message ?? '') !== '' && (
          <>
            <div
              className="fr"
              style={{
                alignItems: 'center',
                marginTop: '15px',
                marginBottom: '20px',
                width: '100%',
              }}
            >
              <div style={{ flex: '0 0 70px' }}>
                <CircleAlert width={30} height={30} strokeWidth={1.5} />
              </div>
              Error Details:
            </div>
            <div className="fr" style={{ width: '100%', marginBottom: '10px' }}>
              <div
                className="border-colour"
                style={{
                  flex: '1 1',
                  overflowWrap: 'anywhere',
                  borderRadius: '10px',
                  paddingLeft: '20px',
                  padding: '10px',
                  margin: '0px 10px 0px 70px',
                }}
                data-faint-border="T"
              >
                {addLineBreaks(sDisplayedStagingJob.error_message?.message)}
              </div>
              <div className="fc" style={{ flex: '0 0 40px', marginRight: '10px' }}>
                <div>
                  <MiniButton
                    className="text-colour"
                    data-mini-button="T"
                    title="View logs"
                    disabled={
                      props.logsFocusRow === undefined ||
                      getLogIndexByJobID(sDisplayedStagingJob.job_id) === undefined ||
                      sDisplayedStagingJob.error_message === undefined
                    }
                    style={{ marginBottom: '10px' }}
                    onClick={() => {
                      if (props.logsFocusRow) {
                        props.logsFocusRow.current = `log_table_row_${getLogIndexByJobID(sDisplayedStagingJob.job_id)}`;
                      }
                      dispatch(setSelectedTool(ToolType.LOGS));
                      navigate(toolTypeToPath[ToolType.LOGS]);
                    }}
                  >
                    <ScrollText width={30} height={30} strokeWidth={1.5} />
                  </MiniButton>
                </div>
                <div>
                  <MiniButton
                    className="text-colour"
                    data-mini-button="T"
                    title="Copy error message"
                    onClick={() =>
                      navigator.clipboard.writeText(
                        sDisplayedStagingJob.error_message?.message ?? '',
                      )
                    }
                  >
                    <Copy width={30} height={30} strokeWidth={1.5} />
                  </MiniButton>
                </div>
              </div>
            </div>
          </>
        )}
        <div
          className="fr"
          style={{
            alignItems: 'center',
            marginTop: '15px',
            marginBottom: '30px',
            width: '100%',
          }}
        >
          <div style={{ flex: '0 0 70px' }}>
            <Files width={30} height={30} strokeWidth={1.5} />
          </div>
          Data file(s) in this job:
        </div>
        {sDisplayedStagingJob.containers.map((container, containerIndex) => (
          <Item key={containerIndex} item={container} type="CONTAINER" expanded={true} />
        ))}
        {sDisplayedStagingJob.datasets.map((dataset, datasetIndex) => (
          <Item key={datasetIndex} item={dataset} type="DATASET" expanded={true} />
        ))}
        {sDisplayedStagingJob.files.map((file, fileIndex) => (
          <Item key={fileIndex} item={file} type="FILE" expanded={true} />
        ))}
      </Scrollbox>
      <div className="fr" style={{ width: '100%', justifyContent: 'right' }}>
        {props.mode === 'close' && (
          <ToolButton
            key={t('Close')}
            name="close-dialog"
            onClick={() => dispatch(setDisplayedStagingJob(undefined))}
            tooltip="Close this information panel"
            text={t('Close')}
            type={ToolButtonType.SECONDARY}
          />
        )}
        {props.mode === 'back' && (
          <ToolButton
            key={t('Back')}
            name="back-dialog"
            onClick={() => dispatch(setDisplayedStagingJob(undefined))}
            tooltip="Back to session status"
            text={t('Back')}
            type={ToolButtonType.SECONDARY}
          />
        )}
      </div>
    </div>
  );
  //
} // JobDetails

//  --------------------------------------------------------------------------
//
//  J O B S T A T U S   C O M P O N E N T
//
//  Composes JobList and JobDetails for the Data Transfer Jobs page.
//
//  --------------------------------------------------------------------------

export default function JobStatus(props: {
  logsFocusRow: React.MutableRefObject<string | undefined>;
  standaloneComponent?: boolean; // default = true
}) {
  //
  const sDisplayedStagingJob: JobType | undefined = useAppSelector(
    (state) => state.fileBrowser.displayedStagingJob,
  );

  return (
    <div
      className="panel-area fr"
      style={{ flex: '1 1 265px', height: '100%', width: '100%' }}
    >
      <JobList
        logsFocusRow={props.logsFocusRow}
        standaloneComponent={props.standaloneComponent}
      />
      {props.standaloneComponent !== false && sDisplayedStagingJob !== undefined && (
        <JobDetails logsFocusRow={props.logsFocusRow} mode="close" />
      )}
    </div>
  );
  //
} // JobStatus
